export type AccountType = 'individual' | 'business_basic' | 'business_pro' | 'business_enterprise' | 'collector';

export const BUSINESS_ACCOUNT_TYPES: AccountType[] = [
  'business_basic',
  'business_pro',
  'business_enterprise',
];

export interface BrandConfig {
  companyName: string;
  logoUrl: string;
  primaryColor: string;
  secondaryColor: string;
  warehouseAddresses?: WarehouseAddress[];
}

export type WeekdayCode = 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun';

export interface TimeWindow {
  startTime: string;
  endTime: string;
}

export interface OperatingHourDay {
  weekday: WeekdayCode;
  isOpen: boolean;
  timeWindows: TimeWindow[];
}

export interface WarehouseAddress {
  id?: string;
  label: string;
  addressLine: string;
  city: string;
  state: string;
  zipCode?: string | null;
  countryCode?: string;
  latitude?: number | null;
  longitude?: number | null;
  isDefault?: boolean;
  operatingHours?: OperatingHourDay[] | null;
}

export function isBusinessAccount(accountType?: string): boolean {
  return BUSINESS_ACCOUNT_TYPES.includes(accountType as AccountType);
}

export function isCollectorAccount(accountType?: string): boolean {
  return accountType === 'collector';
}

export function isB2BAccount(accountType?: string): boolean {
  return isBusinessAccount(accountType) || isCollectorAccount(accountType);
}

export type PackageStatus =
  | 'pending_hn'
  | 'pending'
  | 'assigned'
  | 'pickup_in_progress'
  | 'collected'
  | 'in_transit'
  | 'at_warehouse'
  | 'last_mile'
  // Jett-delivery virtual statuses (USA side)
  | 'jett_assigned'
  | 'jett_pickup_in_progress'
  | 'jett_collected'
  | 'jett_in_transit'
  | 'delivery_failed'
  | 'delivered'
  | 'canceled';

/**
 * High-level phase of a package's journey, returned by the backend alongside `status`.
 *   hn_collection     – package is in Honduras being picked up by a collector
 *   hn_to_usa         – physically in transit HN → USA (plane/truck)
 *   usa_awaiting_jett – arrived at USA warehouse, waiting for a Jett to accept it
 *   usa_jett_delivery – a USA Jett is handling the last-mile delivery
 */
export type PhaseContext =
  | 'hn_collection'
  | 'hn_to_usa'
  | 'usa_awaiting_jett'
  | 'usa_jett_delivery';

export interface AvailableJettItinerary {
  itineraryId: string;
  departureDatetime: string;
  travelType: string;
  originCity?: string;
  originAddress?: string;
  destinationCity?: string;
  destinationAddress?: string;
  availableCapacity?: number;
  flightNumber?: string;
}

export type JettApprovalStatus = 'none' | 'pending' | 'accepted' | 'rejected';

export interface AvailableJett {
  jettUserId: string;
  firstName: string;
  lastName: string;
  profilePicture: string;
  alreadyRequested: boolean;
  approvalStatus: JettApprovalStatus;
  itineraries: AvailableJettItinerary[];
}

export interface B2BDashboardStats {
  total: number;
  pending: number;
  inCollection: number;
  inTransit: number;
  delivered: number;
  canceled: number;
  deliveryFailed: number;
}

export interface B2BRecentPackage {
  id: string;
  trackingCode: string | null;
  recipientName: string | null;
  hondurasDropoffContactName?: string | null;
  hondurasDropoffContactPhonePrefix?: string | null;
  hondurasDropoffContactPhoneNumber?: string | null;
  hondurasDropoffContactEmail?: string | null;
  /** Code value used for business logic (canMarkAsPaid, normalization). */
  registrationSource?: 'aerobox' | 'collector' | 'hn_to_usa' | 'usa_to_hn' | null;
  /** Human-readable display string returned by the backend, e.g. "Jett Sender". */
  registeredBy?: string | null;
  destinationCity: string | null;
  destinationState: string | null;
  destinationStateCode: string | null;
  destinationAddress?: string | null;
  destinationContactNumber?: string | null;
  weight: number | null;
  status: PackageStatus;
  phaseContext?: PhaseContext | null;
  assignedToId?: string | null;
  paymentCollected?: boolean;
  pendingContext?: 'newly_registered' | 'arrived_usa_ready_for_jett' | null;
  createdAt: string;
  collectorId: string | null;
  collectorName: string | null;
  collectionOperatorId?: string | null;
  name?: string | null;
  description?: string | null;
  originAddress?: string | null;
  originInstructions?: string | null;
  /** true when origin_country_code = 'US' AND destination_country_code = 'US' */
  isUsaToUsa?: boolean;
}

export interface B2BDashboardResponse {
  stats: B2BDashboardStats;
  recentPackages: B2BRecentPackage[];
  pipeline: B2BPipeline;
  transitMap: B2BTransitMap;
  isDemo?: boolean;
}

export interface B2BTransitCollector {
  id: string;
  name: string;
  lastLat: number | null;
  lastLng: number | null;
  lastLocationAt: string | null;
}

export interface B2BTransitPackage {
  id: string;
  trackingCode: string | null;
  recipientName: string | null;
  recipientPhone?: string | null;
  status: PackageStatus;
  collectorId: string | null;
  collectorName: string | null;
  originAddress: string | null;
  destinationAddress: string | null;
  originLatitude: number | null;
  originLongitude: number | null;
  destinationLatitude: number | null;
  destinationLongitude: number | null;
}

export interface B2BTransitMap {
  collectors: B2BTransitCollector[];
  packages: B2BTransitPackage[];
}

// --- Pipeline ---
export interface PipelineColumn {
  count: number;
  packages: B2BRecentPackage[];
}

export interface B2BPipeline {
  pending: PipelineColumn;
  collection: PipelineColumn;
  inTransit: PipelineColumn;
  delivered: PipelineColumn;
  canceled: number;
  deliveryFailed: number;
}

// --- Packages ---
export interface B2BPackagesResponse {
  data: B2BRecentPackage[];
  total: number;
  page: number;
  totalPages: number;
  /** false when every package on this page is USA-to-USA; hide Recolector/Empresa recolectora columns when false */
  showCollectorColumns?: boolean;
}

export interface PackageFilters {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
  state?: string;
  fromDate?: string;
  toDate?: string;
}

// --- Bulk Upload ---
export interface BulkUploadError {
  row: number;
  field: string;
  message: string;
}

export interface ImportBatch {
  id: string;
  fileName: string;
  totalRows: number;
  successCount: number;
  errorCount: number;
  errors: BulkUploadError[];
  status: string;
  createdAt: string;
}

// --- Reports ---
export interface StateReport {
  stateCode: string;
  stateName: string;
  count: number;
  delivered: number;
  pending: number;
  inTransit: number;
}

export interface StatusReport {
  status: PackageStatus;
  count: number;
  percentage: number;
}

export interface ReportSummary {
  totalPackages: number;
  totalDelivered: number;
  deliveryRate: number;
  totalWeight: number;
  /** Average days from creation to delivery (delivered packages only). */
  avgDeliveryDays: number;
}

export interface WarehouseEvidenceMissingPackage {
  id: string;
  trackingCode: string | null;
  recipientName: string | null;
  status: PackageStatus;
  createdAt: string;
}

export interface WarehouseEvidenceReport {
  totalEligiblePackages: number;
  packagesWithWarehouseEvidence: number;
  missingEvidenceCount: number;
  complianceRate: number;
  missingEvidencePackages: WarehouseEvidenceMissingPackage[];
  operatorBreakdown: Array<{
    operatorUuid: string;
    operatorName: string;
    operatorEmail: string;
    evidenceCount: number;
    uniquePackages: number;
  }>;
}

export interface ReportDateFilters {
  fromDate?: string;
  toDate?: string;
}

// --- Collectors ---
export interface Collector {
  id: string;
  name: string;
  phone: string;
  phonePrefix: string;
  countryCode: string;
  email: string | null;
  userId: string | null;
  userEmail: string | null;
  zone: string | null;
  isActive: boolean;
  createdAt: string;
  /** Last known GPS latitude (from collector device) */
  lastLat: number | null;
  /** Last known GPS longitude (from collector device) */
  lastLng: number | null;
  /** ISO timestamp of last GPS fix */
  lastLocationAt: string | null;
}

export interface CollectorStats {
  pendingPickup: number;
  inProgress: number;
  collectedToday: number;
  weeklyFailedPickupsCurrent?: number;
  weeklyFailedPickupsPrevious?: number;
  weeklyAvgCollectionHoursCurrent?: number;
  weeklyAvgCollectionHoursPrevious?: number;
}

export interface CollectorPackage {
  id: string;
  trackingCode?: string | null;
  pendingContext?: 'newly_registered' | 'arrived_usa_ready_for_jett' | null;
  collectorId: string | null;
  recipientName: string | null;
  destinationCity: string | null;
  destinationState: string | null;
  weight: number | null;
  status: PackageStatus;
  createdAt: string;
  /** Pickup address (origin) — used for Google Maps navigation */
  originAddress: string | null;
  /** Pickup instructions provided by operator */
  originInstructions?: string | null;
  /** Package name/description shown in QR modal */
  name: string | null;
  /** Recipient phone number */
  destinationContactNumber: string | null;
  /** Manual pickup order used by collector route planning */
  pickupOrder?: number | null;
  /** Optional scheduled pickup datetime (ISO) */
  scheduledAt?: string | null;
  originLatitude?: number | null;
  originLongitude?: number | null;
}

export interface CollectorRouteStop {
  id: string;
  recipientName: string | null;
  originAddress: string | null;
  originLatitude: number | null;
  originLongitude: number | null;
  status: PackageStatus;
  pickupOrder: number;
  scheduledAt: string | null;
}

export interface CollectorRoutePlan {
  collectorLocation: { lat: number; lng: number } | null;
  totalStops: number;
  mapUrl: string | null;
  stops: CollectorRouteStop[];
}

export interface B2BTrackingEvent {
  id: string;
  eventType: string;
  timestamp: string;
  imageUrl?: string | null;
  referenceImage?: string | null;
  handledByUser?: {
    firstName: string;
    lastName: string;
  } | null;
}

export interface LinkCollectorRequest {
  email: string;
}

export interface B2BProfileUpdateRequest {
  companyName?: string;
  logoUrl?: string;
  primaryColor?: string;
  secondaryColor?: string;
  warehouseAddresses?: WarehouseAddress[];
}

export interface CreateCollectorRequest {
  name: string;
  phone: string;
  phonePrefix: string;
  countryCode: string;
  email?: string;
  zone: string;
}

export interface UpdateCollectorRequest extends Partial<CreateCollectorRequest> {
  isActive?: boolean;
}

export interface CollectorZone {
  id: string;
  operatorId: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePackageRequest {
  destinationContact: string;
  destinationContactNumber: string;
  hondurasDropoffContactName: string;
  hondurasDropoffContactPhonePrefix: string;
  hondurasDropoffContactPhoneNumber: string;
  hondurasDropoffContactEmail?: string;
  destinationCity: string;
  destinationState: string;
  destinationAddress: string;
  destinationZipCode?: string;
  destinationLatitude?: number;
  destinationLongitude?: number;
  weight: number;
  originAddress: string;
  originLatitude?: number;
  originLongitude?: number;
  name: string;
  description?: string;
  originInstructions?: string;
  /** Fee de cobertura seleccionado: 0 | 5 | 10 | 15 (USD). Default: 0 */
  coverageFee?: 0 | 5 | 10 | 15;
  /** true si el paquete es de tecnología (requerido para coverageFee = 15) */
  isTechnologyPackage?: boolean;
}

export interface PackageLabelExtractionResponse {
  draft: {
    destinationContact: string | null;
    destinationContactNumber: string | null;
    destinationCity: string | null;
    destinationState: string | null;
    destinationAddress: string | null;
    destinationZipCode: string | null;
    weight: number | null;
    originAddress: string | null;
    name: string | null;
    description: string | null;
    originInstructions: string | null;
  };
  confidence: number;
  missingFields: string[];
}

export interface AssignCollectorRequest {
  /** Set to `null` to unassign. */
  collectorId: string | null;
}

export interface CollectionCompany {
  uuid: string;
  name: string;
  email: string;
  warehouseAddresses?: WarehouseAddress[];
}

export interface AssignCollectionCompanyRequest {
  /** Set to `null` to unassign. */
  collectionOperatorId: string | null;
}

export interface UpdatePackageRequest {
  destinationContact?: string;
  destinationContactNumber?: string;
  hondurasDropoffContactName?: string;
  hondurasDropoffContactPhonePrefix?: string;
  hondurasDropoffContactPhoneNumber?: string;
  hondurasDropoffContactEmail?: string;
  destinationCity?: string;
  destinationState?: string;
  destinationAddress?: string;
  destinationZipCode?: string;
  weight?: number;
  originAddress?: string;
  name?: string;
  description?: string;
  originInstructions?: string;
}

export type WarehouseFlowStatus = 'collected' | 'in_transit' | 'pending';

export interface B2BPaymentIntentResponse {
  clientSecret: string;
  paymentIntentId: string;
  /** Total amount charged to the client, in USD */
  amountDisplay: number;
  /** Amount that will be transferred to the Jett on delivery, in USD */
  jettAmount: number;
  currency: string;
}

export type B2bBusinessRole = 'owner_admin' | 'dispatcher' | 'viewer' | 'collector';

export interface B2bBusinessMember {
  userUuid: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  role: B2bBusinessRole;
  isActive: boolean;
  createdAt: string;
}

export interface B2bBusinessInvitation {
  invitationId: string;
  businessId: string;
  email: string;
  role: B2bBusinessRole;
  token: string;
  expiresAt: string;
  acceptUrl: string;
}

// --- Pricing ---
export interface B2BPricingTier {
  minLb: number;
  maxLb: number | null;
  clientPrice: number;
}

export interface B2BPricingResponse {
  tiers: B2BPricingTier[];
}

export interface B2BPricingEstimateRequest {
  weightLb: number;
  originCountryCode?: string;
  destinationCountryCode?: string;
}

export interface B2BPricingEstimateResponse {
  estimatedPrice: number;
}

// --- Account Statement ---
export type AccountStatementEntryType = 'all' | 'charge' | 'refund';

export interface AccountStatementFilters {
  startDate?: string;
  endDate?: string;
  type?: AccountStatementEntryType;
  page?: number;
  limit?: number;
}

// Account Statement types — aligned with GET /b2b/account-statement response
export interface AccountStatementEntry {
  id: string;
  date: string;
  description: string;
  trackingCode: string | null;
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
  amount: number;
  currency: string;
  status: string;
  receiptUrl?: string | null;
}

export interface AccountStatementSummary {
  totalCharged: number;
  totalRefunded: number;
  netAmount: number;
  currency: string;
}

export interface AccountStatementPagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

export interface AccountStatementResponse {
  transactions: AccountStatementEntry[];
  summary: AccountStatementSummary;
  pagination: AccountStatementPagination;
}
